import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

export default function Connection({ kidId, parentId, deviceFilter }) {
  const isPhone = deviceFilter === 'android';
  const [devices, setDevices] = useState([]);
  const [setupCode, setSetupCode] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [toast, setToast] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    if (!parentId) { setLoading(false); return; }
    (async () => {
      setProgress(30);
      let query = supabase.from('devices').select('*')
        .eq('parent_id', parentId);
      if (kidId) query = query.eq('kid_id', kidId);
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) { console.error('Devices fetch error:', error); setLoading(false); return; }
      setProgress(90);
      setDevices(data || []);
      setProgress(100);
      setLoading(false);
    })();
  }, [parentId, kidId]);

  // Auto-refresh device status every 30 seconds
  useEffect(() => {
    if (!parentId) return;
    const interval = setInterval(async () => {
      let query = supabase.from('devices').select('*')
        .eq('parent_id', parentId);
      if (kidId) query = query.eq('kid_id', kidId);
      const { data } = await query.order('created_at', { ascending: false });
      setDevices(data || []);
    }, 30000);
    return () => clearInterval(interval);
  }, [parentId, kidId]);

  const connectedDevices = devices.filter(d => d.device_name);

  const generateCode = async () => {
    if (!kidId) {
      showToast('Please add a child first using the + button in the sidebar');
      return;
    }
    setCodeLoading(true);
    try {
      const { data, error } = await supabase.rpc('generate_setup_code', { p_kid_id: kidId });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setSetupCode(data.code);
      showToast('Setup code generated!');
    } catch (e) {
      // Fallback: try parent-based generation
      try {
        const { data, error } = await supabase.rpc('generate_setup_code_for_parent', { p_parent_id: parentId });
        if (error) throw error;
        setSetupCode(data?.code);
        showToast('Setup code generated!');
      } catch (e2) {
        showToast('Error: ' + (e2.message || e.message || 'Could not generate code'));
      }
    }
    setCodeLoading(false);
  };

  if (loading) return (
    <div className="loading-container">
      <div className="loading-icon">{'\uD83D\uDD17'}</div>
      <div className="loading-title">Loading devices...</div>
      <div className="loading-bar-track">
        <div className="loading-bar-fill" style={{ width: `${progress}%` }} />
      </div>
      <div className="loading-percent">{progress}%</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Device Connection</h1>
          <p className="page-subtitle">Connect and monitor your child's {isPhone ? 'Android phone' : 'Mac'}</p>
        </div>
      </div>

      {/* Connection Status */}
      {connectedDevices.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {connectedDevices.map(d => {
            const lastSeen = d.last_seen_at ? new Date(d.last_seen_at) : null;
            const isOnline = lastSeen && (Date.now() - lastSeen.getTime()) < 5 * 60 * 1000;
            return (
              <div key={d.id} className={`status-banner ${isOnline ? 'online' : 'offline'}`}>
                <div className="status-icon"
                  style={{ background: isOnline ? '#22c55e' : '#ef4444', borderRadius: 12 }}>
                  {isOnline ? '🟢' : '🔴'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: isOnline ? '#166534' : '#991b1b' }}>
                    {d.device_name}
                  </div>
                  <div style={{ fontSize: 13, color: isOnline ? '#16a34a' : '#dc2626', marginTop: 2 }}>
                    {isOnline
                      ? 'Online — Agent is running and monitoring'
                      : `Offline — Last seen: ${lastSeen
                          ? lastSeen.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                          : 'Never'}`
                    }
                  </div>
                </div>
                <span className={`badge ${isOnline ? 'badge-green' : 'badge-red'}`}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="status-banner none" style={{ marginBottom: 24 }}>
          <div className="status-icon" style={{ background: '#e2e8f0', borderRadius: 12 }}>💻</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#475569' }}>No device connected</div>
            <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
              Follow the steps below to connect your child's {isPhone ? 'Android phone' : 'Mac'}
            </div>
          </div>
        </div>
      )}

      {/* Setup Steps */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">How to Connect</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Step 1 */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              background: '#dbeafe', color: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800,
            }}>1</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Generate a Setup Code</div>
              <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>
                Click the button below to get a 6-digit code. It's valid for 15 minutes.
              </p>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn" onClick={generateCode} disabled={codeLoading}
                  style={{ marginTop: 0 }}>
                  {codeLoading ? 'Generating...' : '🔑 Generate Setup Code'}
                </button>
                {setupCode && (
                  <div style={{
                    background: '#f0fdf4', border: '2px solid #22c55e', borderRadius: 12,
                    padding: '8px 20px', display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{
                      fontSize: 28, fontWeight: 900, letterSpacing: 6,
                      color: '#166534', fontFamily: 'monospace',
                    }}>
                      {setupCode}
                    </span>
                    <span style={{ fontSize: 11, color: '#16a34a' }}>Valid 15 min</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f1f5f9' }} />

          {/* Step 2 */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              background: '#dbeafe', color: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800,
            }}>2</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
                {isPhone ? "Install on Child's Phone" : "Install on Child's Mac"}
              </div>
              <p style={{ fontSize: 13, color: '#64748b' }}>
                {isPhone
                  ? <>Download <strong>ParentsProtection.apk</strong> on your child's Android phone and install it.</>
                  : <>Download <strong>ParentsProtection.dmg</strong> on your child's Mac, open it, and drag the app to Applications.</>
                }
              </p>
            </div>
          </div>

          <div style={{ borderTop: '1px solid #f1f5f9' }} />

          {/* Step 3 */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              background: '#dbeafe', color: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 800,
            }}>3</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Enter the Code</div>
              <p style={{ fontSize: 13, color: '#64748b' }}>
                {isPhone
                  ? "Open the ParentsProtection app on the child's phone and enter the 6-digit code. The device will appear above once connected."
                  : "Open ParentsProtection on the child's Mac and enter the 6-digit code when prompted. The device will appear above once connected."
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Help */}
      <div className="card">
        <div className="card-title">Troubleshooting</div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.8 }}>
          <p><strong>Device shows offline?</strong> — Make sure ParentsProtection is running on the child's Mac. Check Activity Monitor for "ParentsProtectionAgent".</p>
          <p style={{ marginTop: 8 }}><strong>Code not working?</strong> — Codes expire after 15 minutes. Generate a new one and try again.</p>
          <p style={{ marginTop: 8 }}><strong>Need to grant permissions?</strong> — The setup wizard will guide you through granting Screen Recording and Accessibility permissions.</p>
        </div>
      </div>

      {/* Toast */}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
